import { getCacheLocale, useTranslation, MCurrency } from '@sailor/i18n-web'
import qs from 'qs'
import { useRecoilState, useRecoilValue } from 'recoil'
import { useRef, useEffect } from 'react'
import { ShopDetailVo, ShopSummaryVo, BrandInfoVo, ShopInfoVo, BrandListRes, ShopListRes, VSetI18nInfosParams, ShopAndBrandInfoVoRes } from '@/@types'
import {
  accountDetailInfoAtom,
  shopListAtom,
  shopInfoAtom,
  ignoreMenuListAtom,
  brandListAtom,
  brandInfoAtom,
  sideMenuAdInfoAtom,
  shopInfoFinishedAtom,
  settledShopListAtom,
  shopAndAccountInfoAtom,
  merchantModeAtom,
  selectShopIdsAtom,
  shopSelectorModeAtom,
} from '@/common/recoil/atom'
import { currentShopIdSelector, currentBrandIdSelector } from '@/common/recoil/atom/selector'
import mainApi, { adApi, openApi } from '@/common/api/urls'
import { ACCOUNT_TYPE, SHOP_STATUS } from '@RootCommon/constants/shopInfo'
import { ALL_SHOP, ALL_BRAND, DEFAULT_BRAND, ERROR_SHOP, AUTH_MENU_KEY, MENU_AUTH_STATUS, ON_BRAND_INFO } from '@/common/constants'
import {
  getLoginShopid,
  setLoginShopid,
  setLoginShopInfo,
  getLoginUid,
  getLoginUinfo,
  setLoginShopList,
  getLoginShopInfo,
  setLoginBrandInfo,
  setLoginBrandid,
  setLoginBrandList,
  getLoginBrandList,
  getLoginShopList,
  getLoginAccountId,
  setLoginAccountId,
  isGroupAcct,
  isSingleAcct,
  setCityId,
  setRegion,
  setCurrency,
  getLanguage,
  isGroupOrBrandAcct,
  getAccountInfo,
  getIsOrderManager,
  getEnv,
  getRegionByCookie,
  MerchantModeEnum,
  getShopIDs,
  ShopSelectorType,
} from '@RootCommon/utils'
import { find, without } from 'lodash'
import { commonRequestAll, commonRequest } from '@/common/utils/request'
import { owlAddJsInfo, owlReportMetrics, owlAddResourceError, owlReportMetricsWeb } from '@RootCommon/utils/owl'
import { setCookie } from '@utiljs/cookie'
import { AD_GUIDE_COMP_FILTER, OperationTypeIds, NO_BRAND_ID, NO_BRAND_ACC_ID } from '@/common/constants'
import { getAccountShopListApi, getSettledShopListApi, getShopAndBrandInfo } from '@/common/api/shopInfoApi'
import { setCompassRegion } from '@sailor/compass'
import { fetchEntityLogoAndMainPic } from '../api/brandInfo'

const isOrderManager = getIsOrderManager()

const currentLang = getCacheLocale()
// 不展示的门店状态， 接口已过滤，可废弃
const hiddenStatus = {
  [ACCOUNT_TYPE.SINGLE_SHOP]: [SHOP_STATUS.INIT],
  [ACCOUNT_TYPE.SUB_SINGLE_SHOP]: [SHOP_STATUS.INIT],
  [ACCOUNT_TYPE.MERCHANT]: [SHOP_STATUS.INIT, SHOP_STATUS.OFFLINE],
  [ACCOUNT_TYPE.SUB_MERCHANT]: [SHOP_STATUS.INIT, SHOP_STATUS.OFFLINE],
  [ACCOUNT_TYPE.BRAND_GROUP]: [SHOP_STATUS.INIT, SHOP_STATUS.OFFLINE],
  [ACCOUNT_TYPE.SUB_BRAND_GROUP]: [SHOP_STATUS.INIT, SHOP_STATUS.OFFLINE],
  [ACCOUNT_TYPE.GROUP]: [SHOP_STATUS.INIT, SHOP_STATUS.OFFLINE],
  [ACCOUNT_TYPE.SUB_GROUP]: [SHOP_STATUS.INIT, SHOP_STATUS.OFFLINE],
}

// 展示子账号管理的： 客户类型账号、单门店、品牌分组、集团账号、加盟商账号，所有子账号类型不展示
const hasSubAccountTypes = [
  ACCOUNT_TYPE.MERCHANT,
  ACCOUNT_TYPE.BRAND_GROUP,
  ACCOUNT_TYPE.GROUP,
  ACCOUNT_TYPE.SINGLE_SHOP,
  ACCOUNT_TYPE.CUSTOMER_GROUP,
  ACCOUNT_TYPE.CUSTOMER,
  ACCOUNT_TYPE.FRANCHISEE,
]

const pageSize = 100

// 会刷新页面，初始化周期内只调用一次，关联i18n相关参数变动
// 相关功能点：账号登录页url带过来的cityid和region与登录账号实际的不一致，此时url上的信息要变更
// 强依赖updateShopUserInfo中，将capitalCityId给到cityId: capitalCityId，才能正确更新url上的cityId
export function setI18nInfos ({ locale, cityId, region, currency, forceRefresh, noRefresh }: VSetI18nInfosParams) {
  // getCityId,getRegion会优先从url获取值，初次加载或变更过程中，信息还未更新到url时就使用的情况。
  if (cityId) {
    setCityId(cityId)
    setCookie('cityId', String(cityId))
    // i18nRef.current.cityId = cityId //
  }
  if (region) {
    setRegion(region)
    setCookie('region', region)
    setCompassRegion(region)
    // i18nRef.current.region = region
  }
  if (currency) {
    setCurrency(currency)
    // i18nRef.current.currency = currency // 阿语字符串，会报non ISO-8859-1 code
  }
  if (noRefresh || (!location.search && !forceRefresh)) return
  const searchMap = qs.parse(location.search, { ignoreQueryPrefix: true })
  // url无i18n相关参数则不刷新
  if (searchMap.locale || searchMap.cityId || searchMap.region || searchMap.currency || forceRefresh) {
    searchMap.locale && locale && (searchMap.locale = locale)
    searchMap.region && region && (searchMap.region = region)
    searchMap.cityId && cityId && (searchMap.cityId = String(cityId))
    searchMap.currency && currency && (searchMap.currency = currency)
    // url上有i18n相关参数，与门店不匹配时会更新url刷新页面
    // TODO：是否会有循环刷新问题
    const newSearch = `?${qs.stringify(searchMap)}`
    window.location.search !== newSearch && (window.location.search = `?${qs.stringify(searchMap)}`)
  }
}

// 获取门店列表，依赖acctId
export async function getAccountShopList (iacctId?, shopName?, accountType?) {
  const requestParams = { acctIds: !iacctId || iacctId === '0' ? undefined : `${iacctId}`, shopName, pageNo: 1, pageSize }
  const shopListRes = await getAccountShopListApi(
    requestParams,
    // { headers: { ...(i18nRef.current || {}) } } // getRegion会优先从url获取值，初次加载时，账号的region还未更新到url。 =》 已改为优先从storage获取的：getStorageRegion
  )

  owlReportMetricsWeb({ shop_list_success_rate: Number(shopListRes?.data?.list?.length > 0) })

  if (shopListRes?.code !== 0) {
    throw new Error('获取门店列表异常')
  }
  const data0 = shopListRes.data || ({} as ShopListRes['data'])
  const { list = [], total } = data0
  const data = { ...data0 }
  if (total > 100) {
    const resArr = await Promise.all(
      Array(Math.ceil(+total / pageSize) - 1)
        .fill('')
        .map((i, index) => commonRequestAll.post(mainApi.getShopListByAccount, { acctIds: !iacctId || iacctId === '0' ? undefined : `${iacctId}`, shopName, pageNo: index + 2, pageSize })),
    )
    data.list = list.concat(...resArr.map(res => res?.data?.list || []))
  } else if (total === 0 && !shopName && shopName !== 0) {
    // 总数为0，没有输入搜索词才上报
    owlReportMetrics(
      {
        B_PC_ACCOUNT_NO_AUTH: 1,
      },
      { reason: 3 }, // 3 无门店
    )
  }
  return data || {}
}

const getUpdateI18nInfos =
  (preI18nInfo = {}) =>
    (i18nInfo = {}) =>
      setI18nInfos({
        ...getLoginUinfo(),
        ...preI18nInfo,
        ...i18nInfo,
      })


// 信息接口顺序：
// updateAccountDetailAndShopInfoV2：账号detail(accountId cityId region) ->
// getAccountShopList：门店shoplist
export default function () {
  const { t } = useTranslation()
  const getBrandI18n = (brandTotal = 0) => {
    if (brandTotal === 0) {
      return t('B_PC_Group_select_brand_all_brands', { defaultValue: 'All brands' })
    } else {
      return t('B_PC_Group_select_all_brands', { brand_num: brandTotal, defaultValue: `All brands (${brandTotal})` })
    }
  }
  const getShopI18n = (shopTotal = 0) => {
    if (shopTotal === 0) {
      return t('bteam_sidebar_allstores', { defaultValue: 'All restaurants' })
    } else {
      return t('B_PC_Group_select_all_stores', { stores_num: shopTotal, defaultValue: `All restaurants (${shopTotal})` })
    }
  }

  const ALL_SHOP_I18N = { ...ALL_SHOP, name: getShopI18n(0), nameI18nKey: 'B_PC_Group_select_all_stores' }
  const ALL_BRAND_I18N = { ...ALL_BRAND, name: getBrandI18n(0), nameI18nKey: 'B_PC_Group_select_all_brands' }
  const SEARCH_SHOP_I18N = { ...ALL_SHOP, name: getShopI18n(0), nameI18nKey: 'B_PC_Group_select_all_stores' }
  const SEARCH_BRAND_I18N = { ...ALL_BRAND, name: getBrandI18n(0), nameI18nKey: 'B_PC_Group_select_all_brands' }
  const ERROR_SHOP_I18N = { ...ERROR_SHOP, name: `${t('bteam_sidebar_nostore', '无可用门店')}`, nameI18nKey: 'bteam_sidebar_nostore' }

  const [ignoreMenuList, setIgnoreMenuList] = useRecoilState(ignoreMenuListAtom)
  const accountDetailInfo = useRecoilValue(accountDetailInfoAtom)
  const [isShopInfoFinished, setIsShopInfoFinished] = useRecoilState(shopInfoFinishedAtom)
  const [shopList, setShopList] = useRecoilState(shopListAtom)
  const [brandList, setBrandList] = useRecoilState<Array<BrandInfoVo>>(brandListAtom)
  const [currentShop, setCurrentShop] = useRecoilState(shopInfoAtom)
  const [currentbrand, setCurrentBrand] = useRecoilState(brandInfoAtom)
  const currentShopId: number = useRecoilValue(currentShopIdSelector)
  const currentBrandId: string = useRecoilValue(currentBrandIdSelector)
  const [sideMenuAdInfo, setSideMenuAdInfo] = useRecoilState(sideMenuAdInfoAtom)
  const [settledShopList, setSettledShopList] = useRecoilState(settledShopListAtom)
  const [shopAndAccountInfo, setShopAndAccountInfo] = useRecoilState(shopAndAccountInfoAtom)
  const [merchantMode] = useRecoilState(merchantModeAtom)
  const [shopSelectorMode] = useRecoilState(shopSelectorModeAtom)
  const [shopIDs, setShopIDs] = useRecoilState(selectShopIdsAtom)

  const getSideMenuAdInfo = async () => {
    const operationTypeId = +getLoginShopid() ? OperationTypeIds.menuAdLabel : OperationTypeIds.multiShopAdLabel
    try {
      const params = {
        compFilter: AD_GUIDE_COMP_FILTER.guide,
        typeIds: operationTypeId,
      }
      const { guideInfos } = await commonRequest.get(adApi.getAdGuideInfo, { params, autoToast: !isOrderManager })
      const adMenuGuide = find(guideInfos, _guideInfo => _guideInfo.guideType === operationTypeId)
      setSideMenuAdInfo({
        guideModel: adMenuGuide?.guideModel || 0,
        limitExceed: adMenuGuide?.limitExceed || false,
      })
    } catch (err) {
      // get ad menu guide error
    }
  }

  // 路径限定的菜单展示， openapi默认展示
  async function updateMenuShow () {
    const noPermissionMenuKeyList = []
    const path = '/m/web/openapi/authorize'

    const isTestEnv = getEnv() === 'test'
    const region = sessionStorage.getItem('region')

    // BR region 不展示 gcc hk 应用授权管理页
    if (!isTestEnv && region === 'BR' && !ignoreMenuList.includes(AUTH_MENU_KEY.OPENAPI)) {
      noPermissionMenuKeyList.push(AUTH_MENU_KEY.OPENAPI)
      setIgnoreMenuList(p => [...p.concat(noPermissionMenuKeyList)])
    }

    // 当地区不为 BR 巴西时，暂不展示 Mega 商户授权菜单页面
    if (!isTestEnv && region !== 'BR' && !ignoreMenuList.includes(AUTH_MENU_KEY.OPENAPI_APP_ACTIVATION)) {
      noPermissionMenuKeyList.push(AUTH_MENU_KEY.OPENAPI_APP_ACTIVATION)
      setIgnoreMenuList(p => [...p.concat(noPermissionMenuKeyList)])
    }

    // 子账号类型不展示 Mega 商户授权菜单
    if (getLoginUinfo()?.isSubAccountType) {
      setIgnoreMenuList(p => p.includes(AUTH_MENU_KEY.OPENAPI_APP_ACTIVATION) ? p : [...p, AUTH_MENU_KEY.OPENAPI_APP_ACTIVATION])
    }

    /**
     * 应用授权管理入口展示逻辑（GCC HK）
     * 1. 查询后端接口控制菜单显隐
     * 2. 如果当前访问路径是授权页（通过链接访问），默认展示此菜单入口 (避免初始链接授权时无授权记录入口被隐藏)
     */

    if (region !== 'BR') {
      // 链接访问 （1. path 为当前应用授权管理页 path 2. 携带 search 参数 3. 无 hash 路由，app-activation 也为 hash 路由）
      const isDirectLinkAccess = location.pathname === path && location.search && !location.hash

      try {
        const res = await commonRequestAll.post(openApi.getAuthMenuVisible)
        if (res?.code === 0) {
          const visible = res?.data
          // 后端判断不展示，且非当前链接访问时隐藏应用授权管理入口
          if (!visible && !isDirectLinkAccess) {
            setIgnoreMenuList(p => (p.includes(AUTH_MENU_KEY.OPENAPI) ? p : [...p, AUTH_MENU_KEY.OPENAPI]))
          }
        }
      } catch (e) {
        console.log(e, '查询授权列表接口异常')
      }
    }
  }

  // 账号类型限定的菜单展示，子账号-默认不展示
  function updateAccountMenu () {
    const noPermissionMenuKeyList = [AUTH_MENU_KEY.SUBACCOUNT]
    // 默认不展示，要展示得移除。
    // 不展示子账号管理的： 品牌分组子账号，及所有子账号类型
    if (hasSubAccountTypes.includes(accountDetailInfo?.accountType)) {
      setIgnoreMenuList(p => without(p, ...noPermissionMenuKeyList))
    } else {
      setIgnoreMenuList(p => [...p.concat(noPermissionMenuKeyList)])
    }
  }

  // 依赖账号信息就绪
  async function updateAdMenuShow () {
    let noPermissionMenuKeyList = [AUTH_MENU_KEY.AD]
    let showAd = false
    // 没有账号ID不请求
    if (!+getLoginAccountId()) return
    // 单门店类型 没有门店ID不请求
    if (isSingleAcct(accountDetailInfo?.accountType) && !+getLoginShopid()) {
      return
    }
    try {
      const params = {
        shopId: getLoginShopid(),
        targetAcctId: getLoginAccountId(),
      }
      const res = await commonRequestAll.get(mainApi.menuAuthFilterAd, { params })
      if (+res?.data?.showAdMenu === 1) {
        // 展示广告菜单，请求角标接口
        getSideMenuAdInfo()
        noPermissionMenuKeyList = []
        showAd = true
        owlReportMetrics(
          {
            AdEntryShow: 1,
          },
          {
            entry: 'pcMenu',
            from: 'pc',
          },
        )
      }
    } catch (e) {
      // 灰度期间异常情况都入口不可见
    }
    setIgnoreMenuList(p => {
      let newList = p.concat(noPermissionMenuKeyList)
      if (showAd) {
        newList = without(newList, AUTH_MENU_KEY.AD)
      }
      return newList
    })
  }

  async function updateSingMenuAuth () {
    try {
      const shopId = getLoginShopid()
      const needQueryMenuKeys = [AUTH_MENU_KEY.GROWTH, AUTH_MENU_KEY.HEALTHY, AUTH_MENU_KEY.NEWORDER, AUTH_MENU_KEY.MERCHANT_MARKET]
      // 商家商城灰度
      const { data = {} } = await commonRequestAll.post(mainApi.menuAuthFilter, { shopId, authKeyList: needQueryMenuKeys })
      // todo debug
      const nData = {
        ...data,
        // [AUTH_MENU_KEY.MERCHANT_MARKET]: 1,
      }
      const dbHasPermissionMenuKeyList = Object.entries(nData || {})
        .filter(([key, value]) => value === MENU_AUTH_STATUS.HAS_PERMISSION)
        .map(([key]) => key)
      // 需要把忽略的过滤掉
      setIgnoreMenuList(p => {
        const _ignore = without(p, ...dbHasPermissionMenuKeyList)
        return _ignore
      })
    } catch (e) {
      // 灰度期间异常情况都入口不可见
    }
  }

  async function updateCurrentShop (shopId) {
    const storedAccountDetailInfo = getLoginUinfo()
    storedAccountDetailInfo.cityId = storedAccountDetailInfo?.capitalCityId
    if (shopId === ALL_SHOP_I18N.shopId) {
      // 全部门店展示数量
      const loginShopList = getLoginShopList() || []
      const headShopText = getShopI18n(loginShopList?.length)
      setCurrentShop({ ...ALL_SHOP_I18N, name: headShopText } as unknown as ShopDetailVo)  // 默认带门店数量
      setLoginShopInfo(ALL_SHOP_I18N)
      setLoginShopid(ALL_SHOP_I18N.shopId)
      setCookie('shopId', String(ALL_SHOP_I18N.shopId))
      return {
        ...ALL_SHOP_I18N,
        ...storedAccountDetailInfo,
        setI18nInfos: getUpdateI18nInfos(),
      }
    }
    if (!shopId || shopId === -1) {
      setCurrentShop(ERROR_SHOP_I18N as unknown as ShopDetailVo)
      setLoginShopInfo(ERROR_SHOP_I18N)
      setLoginShopid(ERROR_SHOP_I18N.shopId)
      setCookie('shopId', String(ERROR_SHOP_I18N.shopId))
      return {
        ...ERROR_SHOP_I18N,
        ...storedAccountDetailInfo,
        setI18nInfos: getUpdateI18nInfos(),
      }
    }
    const oldShopInfo = getLoginShopInfo()
    // 减少一个请求:queryI18nInfo + 并行请求
    const { shopInfoRes, shopInfoSummary, i18nInfo } = await Promise.allSettled([
      commonRequest.get(mainApi.getShopInfo, {
        params: { shopId },
        autoToast: !isOrderManager,
      }) as ShopDetailVo,
      commonRequest.get(mainApi.getShopSummary, {
        params: { shopId },
        autoToast: !isOrderManager,
      }) as ShopSummaryVo,
    ]).then(results => {
      const [shopInfoRes, shopInfoSummary] = results.map(result => {
        if (result.status === 'fulfilled') {
          return result.value || {}
        } else if (result.status === 'rejected') {
          return {}
        }
      }) as [ShopDetailVo, ShopSummaryVo]
      const shopI18nInfo = shopInfoRes.i18nInfo || {}
      return {
        shopInfoRes,
        shopInfoSummary,
        i18nInfo: {
          locale: getLanguage(),
          cityId: shopI18nInfo.cityId || 810001,
          region: shopI18nInfo.region || process_env_idcDefaultRegion || 'HK',
          currency: shopI18nInfo.currency || MCurrency.getCurrencyByCityId(shopI18nInfo.cityId || 810001),
        },
      }
    })
    const _shop = shopInfoRes?.shopId
      ? {
        ...oldShopInfo,
        ...shopInfoRes,
        ...shopInfoSummary,
        ...i18nInfo,
        shopId: shopInfoRes?.shopId,
        // TODO: 临时兼容，待后端数据完善
        name: currentLang === 'en' && shopInfoRes.enName ? shopInfoRes.enName : shopInfoRes.name,
      }
      : { ...oldShopInfo, ...i18nInfo }
    setCurrentShop(_shop as ShopDetailVo)
    setLoginShopInfo(_shop)
    setLoginShopid(_shop.shopId)
    setCookie('shopId', _shop.shopId)

    return {
      ..._shop,
      setI18nInfos: getUpdateI18nInfos(i18nInfo),
    }
  }

  function updateBrandInfo (newBrandInfo) {
    setCurrentBrand(newBrandInfo)
    setLoginBrandInfo(newBrandInfo)
    setLoginBrandid(newBrandInfo.brandId)
    if (newBrandInfo.acctId === '0') {
      setLoginAccountId(getLoginUid()) // 没有选中品牌时，重置为登录账号id
      setCookie('accountId', getLoginUid())
    } else {
      setLoginAccountId(newBrandInfo.acctId) // 没有选中品牌时，重置为登录账号id
      setCookie('accountId', newBrandInfo.acctId)
    }
  }

  // 品牌组账号或者加盟商账号
  function isBrandAndSubBrandGroupAcct (accountType) {
    let _accountType = accountType
    if (!accountType) {
      const { accountType } = getAccountInfo()
      _accountType = accountType
    }
    return [ACCOUNT_TYPE.BRAND_GROUP, ACCOUNT_TYPE.FRANCHISEE,  ACCOUNT_TYPE.SUB_BRAND_GROUP, ACCOUNT_TYPE.SUB_FRANCHISEE].includes(_accountType)
  }

  // 更新当前品牌信息：集团门店列表点击时 || 门店信息更新时
  async function updateCurrentBrand (brandId, brand?, rtShopInfo?, nextAllBrandList?) {
    const allBrandList = nextAllBrandList || brandList
    const ALLBRANDITEM = { ...(allBrandList[0] || ALL_BRAND_I18N), acctId: getLoginUid() }
    // // 全部品牌 + 全部门店
    let _nextBrand = brand ? brand : allBrandList.find(item => item.brandId === brandId) || ALLBRANDITEM
    // 2025.04,增加：处理集团账号下挂单门店时（门店本身有品牌），品牌列表里找不到，则定位到无品牌项
    if (_nextBrand.brandId === NO_BRAND_ID) {
      _nextBrand = {
        ...ON_BRAND_INFO,
        ..._nextBrand,
        brandId: NO_BRAND_ID,
        acctId: getLoginUid(),
      }
    } else {
      const hasNoBrand = allBrandList.find(item => `${item.brandId}` === NO_BRAND_ID)
      const brandNotInGroupBrands = _nextBrand.brandId === '0' && brandId !== '0' // 2025.04,增加：当前门店所属brandId不在品牌列表范围的情况
      // 当前账号有无品牌项，且当前门店所属brandId不在品牌列表范围的情况
      if (hasNoBrand && brandNotInGroupBrands) {
        _nextBrand = {
          ...ON_BRAND_INFO,
          ..._nextBrand,
          brandId: NO_BRAND_ID,
          acctId: getLoginUid(),
        }
      }
    }
    // 兜底逻辑：全部门店时，_nextBrand生效成了无品牌项， 重置为全部品牌全部门店
    if (_nextBrand.brandId === NO_BRAND_ID && String((rtShopInfo || currentShop).shopId) === '0') {
      _nextBrand = ALLBRANDITEM
    }
    // 正常匹配到品牌，或下挂无品牌的集团账号未匹配到品牌时
    updateBrandInfo(_nextBrand)

    // 如果有品牌ID，则获取品牌logo和主图
    if (isGroupOrBrandAcct?.(accountDetailInfo.accountType) && !getLoginShopid()) {
      let brandId = 0
      const accId = _nextBrand?.acctId || accountDetailInfo.acctId
      const accType = _nextBrand?.acctType || _nextBrand?.accountType || accountDetailInfo.accountType
      if (isBrandAndSubBrandGroupAcct(accType)) {
        brandId = accountDetailInfo.brandGroupId
      } else {
        brandId = Number(_nextBrand.brandId) || Number(accountDetailInfo.brandId) || 0
      }

      const response = await fetchEntityLogoAndMainPic(accId, accType?.toString() || '', brandId.toString() || '')
      if (response) {
        // 提取logo和主图URL以及品牌名称
        const { logoUrl, shopMainPicUrl, name } = response
        // 整合信息
        _nextBrand = {
          ..._nextBrand,
          logoUrl: logoUrl || '',
          shopMainPicUrl: shopMainPicUrl || '',
          curName: name || '',
        }
        setLoginBrandInfo(_nextBrand)
      }
    }
    return _nextBrand
  }
  async function updateCurrentShopList (brandId, brand?) {
    const ALLBRANDITEM = brandList[0] || ALL_BRAND_I18N
    const _nextBrand = brand ? brand : brandList.find(item => item.brandId === brandId) || ALLBRANDITEM
    const { brandTotal, accountType } = _nextBrand.accountType
    // 有选中的品牌时，才会请求门店列表
    const shopListRes = await getAccountShopList(_nextBrand.acctId)
    const { list: shopList, total: shopTotal } = shopListRes || {}
    const validShopList = shopList
      .filter(shop => shop.status && !hiddenStatus[+accountType]?.includes(shop?.status))
      .map(shop => ({ ...shop, shopId: +(shop.shopId || 0), value: +(shop.shopId || 0), name: shop.shopName, shopTotal, brandTotal, accountType, brandId }))
    const ALLSHOPITEM = { ...ALL_SHOP_I18N, name: getShopI18n(shopTotal), shopTotal, brandId }
    const allValidShopList = getAllValidShopList(validShopList, ALLSHOPITEM, accountType)
    setShopList(allValidShopList)
    setLoginShopList(allValidShopList)
    const isCurrentShopIdExist = validShopList.find(item => item.shopId === +currentShopId)
    let _nextShopId = currentShopId
    if (!currentShopId || !isCurrentShopIdExist) {
      const [firstShop] = allValidShopList
      _nextShopId = (firstShop || {}).shopId
    }
    return { shopList: allValidShopList, shopId: _nextShopId }
  }

  // 仅集团账号：点选品牌时，更新门店列表
  async function updateSelectInfo (brandId, brand?, shopSearchText?) {
    let ALLBRANDITEM = brandList[0] || ALL_BRAND_I18N
    let _nextBrand = brand ? brand : brandList.find(item => item.brandId === brandId) || ALLBRANDITEM
    const { brandTotal, accountType } = _nextBrand
    if (brandId === ALLBRANDITEM.brandId || !brandId) {
      // 创建新对象而不是直接修改
      ALLBRANDITEM = { ...ALLBRANDITEM, name: brandTotal ? getBrandI18n(brandTotal) : ALLBRANDITEM.name }
      _nextBrand = ALLBRANDITEM
    }
    setCurrentBrand(_nextBrand)
    // 有选中的品牌时，才会请求门店列表
    const shopListAccountId = _nextBrand.brandId === NO_BRAND_ID ? NO_BRAND_ACC_ID : _nextBrand.acctId
    // const shopListRes = await getAccountShopList(_nextBrand.acctId, shopSearchText)
    const shopListRes = await getAccountShopList(shopListAccountId, shopSearchText)
    const { list: shopList, total: shopTotal } = shopListRes || {}
    const validShopList = shopList
      .filter(shop => shop.status && !hiddenStatus[+accountType]?.includes(shop?.status))
      .map(shop => ({ ...shop, shopId: +(shop.shopId || 0), value: +(shop.shopId || 0), name: shop.shopName, shopTotal, brandTotal, accountType, brandId }))
    const ALLSHOPITEM = { ...ALL_SHOP_I18N, name: getShopI18n(shopTotal), shopTotal, brandId }
    const allValidShopList = [ALLSHOPITEM, ...validShopList] // 总部账号加【全部门店】
    if (!currentShopId) setCurrentShop(ALLSHOPITEM as unknown as ShopDetailVo)  // 未选中门店时，更新展示【全部门店】+ 数量
    setShopList(allValidShopList)
    setLoginShopList(allValidShopList)
    return { shopList: allValidShopList }
  }

  // 门店搜索词变化时，更新门店列表信息, 集团账号依赖品牌id
  async function updateSearchInfo (searchText = '') {
    const loginBrandList = getLoginBrandList() || []
    const loginShopList = getLoginShopList() || []
    const searchNoTextbrandTotal = loginBrandList.length || 0
    const searchBrandI18n = { ...SEARCH_BRAND_I18N, accId: getLoginUid(), brandTotal: searchNoTextbrandTotal }
    searchBrandI18n.name = getBrandI18n(searchNoTextbrandTotal)
    if (searchText === '') {
      setBrandList([searchBrandI18n, ...loginBrandList])
      setShopList([{ ...SEARCH_SHOP_I18N }, ...loginShopList])
      return
    }
    // 有选中的品牌时，才会请求门店列表
    const shopListRes = await getAccountShopList(undefined, searchText)
    const { list: shopList, total } = shopListRes || {}
    const accountType = loginBrandList[0]?.accountType
    const brandIds = shopList.map(item => item.brandId)
    // 筛选出的门店所属品牌，可能为多个品牌，2025.4.28增加可能为非品牌单门店
    const _nextBrandList = loginBrandList.filter(item => {
      if (brandIds.includes(item.brandId)) {
        return true
      } else if (item.brandId === NO_BRAND_ID) {
        // 没有匹配到brandIds, 但匹配到当前选中brandId
        return true
      }
      return false
    })
    const brandTotal = _nextBrandList.length
    // const brandIds = []
    const shopListMap = shopList.map(shop =>
      // brandIds.push(shop.brandId)
      ({ ...shop, shopId: +(shop.shopId || 0), value: +(shop.shopId || 0), name: shop.shopName, shopTotal, brandTotal, accountType }),
    )
    setBrandList([searchBrandI18n, ..._nextBrandList])
    // 过滤出当前品牌下的门店
    let validShopList = shopList.filter(shop => currentBrandId === NO_BRAND_ID || currentBrandId === SEARCH_BRAND_I18N.brandId || shop.brandId === currentBrandId)
    const shopTotal = validShopList.length
    validShopList = validShopList.map(shop => ({ ...shop, shopId: +(shop.shopId || 0), value: +(shop.shopId || 0), name: shop.shopName, shopTotal, brandTotal, accountType }))
    const allValidShopList = [{ ...SEARCH_SHOP_I18N }, ...(!validShopList.length && shopListMap.length ? shopListMap : validShopList)] // 总部账号加【全部门店】
    setShopList(allValidShopList)
    return { shopList: allValidShopList }
  }

  function getAllValidShopList (validShopList, all, accountType) {
    // 单店不添加【全部门店】
    return isSingleAcct(accountType) ? (validShopList.length ? validShopList : [ERROR_SHOP_I18N]) : [{ ...all }, ...validShopList]
  }

  // 设置finished前确保门店状态已设置上
  function setIsShopInfoFinishedAciton (status, infos?) {
    // TODO: 收口setSession，目前较分散
    const  {
      shopId,
      shopInfo,
      brandId,
      brandInfo,
    } = infos || {}
    shopId && setLoginShopid(shopId)
    shopInfo && setLoginShopInfo(shopInfo)
    brandInfo && setLoginBrandInfo(brandInfo)  // 传入对象
    brandId && setLoginBrandid(brandId)        // 传入ID
    setIsShopInfoFinished(status)
  }

  // 依赖账号信息就绪： 两个接口updatePassportUserInfo、updateShopUserInfo
  async function updateAccountDetailAndShopInfoV2 () {
    try {
      const { acctId, accountType } = accountDetailInfo // TODO： state异步更新后，此处值是否更新？
      let brandListRes: BrandListRes
      let shopListRes: ShopListRes['data']
      let _nextBrandId = currentBrandId || '0'
      let _nextBrand: BrandInfoVo = { ...DEFAULT_BRAND, acctId }
      let brandTotal = 1
      let _nextShopId = 0
      let _nextShop: ShopInfoVo
      let validShopList = []
      let allValidShopList = []
      let allValidBrandList = brandList

      const getShopList = async (brandInfo?, validBrandList?) => {
        const __brandId = brandInfo?.brandId
        shopListRes = await getAccountShopList(brandInfo?.acctId, '', accountType)
        const { list: shopList, total: shopTotal } = shopListRes || {}
        validShopList = shopList
          .filter(shop => shop.status && !hiddenStatus[+accountType]?.includes(shop?.status))
          .map(shop => ({ ...shop, shopId: +(shop.shopId || 0), value: +(shop.shopId || 0), name: shop.shopName, shopTotal, brandTotal, accountType }))
        const ALLSHOPITEM = { ...ALL_SHOP_I18N, name: getShopI18n(shopTotal), shopTotal, brandId: _nextBrandId }
        allValidShopList = getAllValidShopList(validShopList, ALLSHOPITEM, accountType)
        setShopList(allValidShopList)
        setLoginShopList(allValidShopList)
        _nextShop = validShopList.find(item => item.shopId === +currentShopId)
        _nextShopId = currentShopId
        if (!currentShopId || !_nextShop) {
          const [firstShop] = allValidShopList
          _nextShop = firstShop || {}
          _nextShopId = _nextShop.shopId
        } else if (validBrandList && (!__brandId || __brandId === '0') && _nextShop?.brandId) {
          // url带shopId打开时，只有门店id,但没有品牌信息
          _nextBrand = validBrandList.find(item => item.brandId === _nextShop.brandId)
          _nextBrandId = _nextShop.brandId
        }
      }
      // 集团账号. 之前页面没有清除的账号、品牌、门店信息有干扰。 没有品牌id，或品牌id不属于当前账号，则默认全部品牌 + 空门店列表
      if (isGroupAcct(accountType)) {
        // 品牌列表
        // brandListRes = await commonRequestAll.get(mainApi.getBindAccountList, { params: { pageNo: 1, pageSize: 100 } }) as BrandListRes
        const brandListParams = { pageNo: 1, pageSize: 100 }
        brandListRes = (await commonRequestAll.get(mainApi.getAccountBrandList, { params: brandListParams })) as BrandListRes

        owlReportMetricsWeb({ brand_list_success_rate: Number(brandListRes?.code === 0) })

        if (brandListRes?.code !== 0) {
          throw new Error('获取品牌列表异常')
        }
        const { list, total } = brandListRes.data || {}
        brandTotal = total
        const ALLBRANDITEM = { ...ALL_BRAND_I18N, name: getBrandI18n(brandTotal), brandTotal, acctId, accountType }
        const validBrandList = list.map(brand => ({ ...brand, name: brand.brandName, value: brand.brandId, brandTotal, accountType }))
        allValidBrandList = [ALLBRANDITEM, ...validBrandList]
        setBrandList(allValidBrandList)
        setLoginBrandList(allValidBrandList)

        // 集团账号下挂单门店时（门店本身有品牌），品牌列表里可能找不到. _nextBrandId此处等于(currentBrandId || '0')
        _nextBrand = allValidBrandList.find(item => item.brandId === _nextBrandId)
        if (!_nextBrand || _nextBrand.brandId === '0') {
          // 没有品牌id，或品牌id不属于当前账号，则默认全部品牌 + 空门店列表
          const hasNoBrand = validBrandList.find(item => `${item.brandId}` === NO_BRAND_ID)
          // if (hasNoBrand && _nextBrandId !== '0') {
          if (hasNoBrand && _nextBrand.brandId === '0' && _nextBrandId !== '0') {
            // 选中无品牌
            const noBrandInfo = {
              ...ON_BRAND_INFO,
              // brandId: _nextBrandId,
              brandId: NO_BRAND_ID,
              // acctId: getLoginAccountId() || getLoginUid(), // getLoginAccountId是否要取无品牌单门店的id？
              acctId: getLoginUid(),
            }
            _nextBrand = noBrandInfo
            _nextBrandId = noBrandInfo.brandId
          } else {
            _nextBrand = ALLBRANDITEM
            _nextBrandId = ALLBRANDITEM.brandId
          }
          if (+currentShopId > 0) {
            // 集团账号，url上有门店id时，需要再多判断一步来选中对应门店。没有品牌,但有门店id：增加根据门店id选中品牌能力。
            await getShopList(_nextBrand, validBrandList)
          }
        } else {
          // 有选中的品牌时，直接请求门店列表
          await getShopList(_nextBrand, validBrandList)
        }
      } else {
        await getShopList(_nextBrand)
      }
      // 立即更新时allValidBrandList还未写入state
      updateCurrentBrand(_nextBrandId, _nextBrand, _nextShop, allValidBrandList)
      // setIsShopInfoFinished(true)
      setIsShopInfoFinishedAciton(true, {
        shopId: _nextShopId,
        shopInfo: _nextShop,
        brandId: _nextBrandId,
        brandInfo: _nextBrand,
      })
      return { shopId: _nextShopId, brandId: _nextBrandId }
    } catch (e) {
      setCurrentShop(ERROR_SHOP_I18N as unknown as ShopDetailVo)
      setShopList([ERROR_SHOP_I18N])
      setIsShopInfoFinishedAciton(true)
      return { shopId: ERROR_SHOP_I18N.shopId, brandId: ALL_BRAND_I18N.brandId }
    }
  }

  // 非集团账号更新门店信息
  async function updateNanGroupShopInfo () {
    try {
      const { acctId, accountType } = accountDetailInfo // TODO： state异步更新后，此处值是否更新？
      let shopListRes: ShopListRes['data']
      let _nextBrandId = currentBrandId || '0'
      let _nextBrand: BrandInfoVo = { ...DEFAULT_BRAND, acctId }
      let brandTotal = 1
      let _nextShopId = 0
      let _nextShop: ShopInfoVo
      let validShopList = []
      let allValidShopList = []

      const getShopList = async (brandInfo?) => {
        shopListRes = await getAccountShopList(brandInfo?.acctId, '', accountType)
        const { list: shopList, total: shopTotal } = shopListRes || {}
        validShopList = shopList
          .filter(shop => shop.status && !hiddenStatus[+accountType]?.includes(shop?.status))
          .map(shop => ({ ...shop, shopId: +(shop.shopId || 0), value: +(shop.shopId || 0), name: shop.shopName, shopTotal, brandTotal, accountType }))
        const ALLSHOPITEM = { ...ALL_SHOP_I18N, name: getShopI18n(shopTotal), shopTotal, brandId: _nextBrandId }
        allValidShopList = getAllValidShopList(validShopList, ALLSHOPITEM, accountType)
        setShopList(allValidShopList)
        setLoginShopList(allValidShopList)
        _nextShop = validShopList.find(item => item.shopId === +currentShopId)
        _nextShopId = currentShopId
        if (!currentShopId || !_nextShop) {
          const [firstShop] = allValidShopList
          _nextShop = firstShop || {}
          _nextShopId = _nextShop.shopId
        }
      }
      await getShopList(_nextBrand)
      updateCurrentBrand(_nextBrandId, _nextBrand)
      // 所有门店状态以finished为基准，此处需要确保信息准确，否则不设置true, 后续结合产品逻辑提示用户重试或者其他提示
      setIsShopInfoFinishedAciton(true, {
        shopId: _nextShopId,
        shopInfo: _nextShop,
      })
      if (!['HK', 'SA'].includes(getRegionByCookie())) {
      // 只有在欧洲账号获取自入驻门店列表，仅在客户连锁账号下展示
        const settledShopListRes = await getSettledShopListApi()
        // 只筛选录入中的门店
        const settledShopList = settledShopListRes?.filter(item => !item.selfEntryShopStatus && item.shopId && item.shopStatus === SHOP_STATUS.INIT)
        setSettledShopList(settledShopList || [])
      }

      return { shopId: _nextShopId, brandId: _nextBrandId }
    } catch (e) {
      setCurrentShop(ERROR_SHOP_I18N as unknown as ShopDetailVo)
      setShopList([ERROR_SHOP_I18N])
      setIsShopInfoFinishedAciton(true) // TODO： 门店信息异常后的处理逻辑
      return { shopId: ERROR_SHOP_I18N.shopId, brandId: ALL_BRAND_I18N.brandId }
    }
  }

  async function updateShopHealthySample () {
    try {
      if (+getLoginShopid() > 0) {
        return
      }
      const params = {
        shopId: getLoginShopid(),
      }
      const data = await commonRequestAll.post(mainApi.postShopHealthyFirstLogin, params)
      return {
        ...data?.data,
      }
    } catch (error) {
      console.error(error, '请求健康度简单信息失败')
    }
  }

  // 多店下品牌分组账号、客户分组账号、客户主账号、品牌主账号 更新门店信息
  async function updateBrandGroupAndCustomerGroupShopInfo () {
    try {
      const { acctId, accountType } = accountDetailInfo // TODO： state异步更新后，此处值是否更新？
      let shopListRes: ShopAndBrandInfoVoRes
      let _nextBrandId = currentBrandId || '0'
      let _nextBrand: BrandInfoVo = { ...DEFAULT_BRAND, acctId }
      let brandTotal = 1
      let _nextShopId = 0
      let _nextShop: ShopInfoVo
      let validShopList = []

      const getShopList = async (brandInfo?) => {
        shopListRes = await getShopAndBrandInfo({ acctId })
        setShopAndAccountInfo(shopListRes)
        const { shopInfoList: shopList } = shopListRes || {}
        validShopList = shopList
          .map(shop => ({ ...shop, shopId: +(shop.shopId || 0), value: +(shop.shopId || 0), name: shop.shopName, shopTotal: shopList.length, brandTotal, accountType }))
        setShopList(validShopList)
        setLoginShopList(validShopList)
        if (merchantMode === MerchantModeEnum.MultiStore) {
          const currentShopIds = getShopIDs()
          const ids = validShopList.map(item => item.shopId.toString())

          if (shopSelectorMode === ShopSelectorType.MultiShopCanNotSelect || (ids.length > 0 && !currentShopIds?.some(id => ids.includes(id)))) {
            setShopIDs(ids)
            sessionStorage.setItem('SHOP_IDS', ids.join(','))
          }
        }
        _nextShop = validShopList.find(item => item.shopId === +currentShopId)
        _nextShopId = currentShopId
        // 门店选择器是单选模式
        if ((merchantMode === MerchantModeEnum.SingleStore || shopSelectorMode === ShopSelectorType.SingleShop) && currentShopId === 0) {
          const [firstShop] = validShopList
          _nextShop = firstShop || {}
          _nextShopId = _nextShop.shopId
        }
      }
      await getShopList(_nextBrand)
      updateCurrentBrand(_nextBrandId, _nextBrand)
      // 所有门店状态以finished为基准，此处需要确保信息准确，否则不设置true, 后续结合产品逻辑提示用户重试或者其他提示
      setIsShopInfoFinishedAciton(true, {
        shopId: _nextShopId,
        shopInfo: _nextShop,
      })

      return { shopId: _nextShopId, brandId: _nextBrandId }
    } catch (e) {
      setCurrentShop(ERROR_SHOP_I18N as unknown as ShopDetailVo)
      setShopList([ERROR_SHOP_I18N])
      setIsShopInfoFinishedAciton(true) // TODO： 门店信息异常后的处理逻辑
      return { shopId: ERROR_SHOP_I18N.shopId, brandId: ALL_BRAND_I18N.brandId }
    }
  }

  return {
    currentBrandId,
    currentbrand,
    currentShopId,
    currentShop,
    shopList,
    brandList,
    updateAccountDetailAndShopInfoV2,
    updateNanGroupShopInfo,
    updateCurrentShop,
    updateCurrentShopList,
    updateCurrentBrand,
    updateSelectInfo,
    updateSearchInfo,
    ignoreMenuList,
    updateMenuShow,
    updateAccountMenu,
    sideMenuAdInfo,
    getSideMenuAdInfo,
    updateAdMenuShow,
    updateSingMenuAuth,
    updateShopHealthySample,
    updateBrandGroupAndCustomerGroupShopInfo,
    shopAndAccountInfo,
    ALL_SHOP_I18N,
  }
}
